/**
 * ImportWallet — Import wallet from mnemonic phrase.
 *
 * Features: word-by-word input, paste support, BIP-39 validation, password setup.
 */

import { useState, useCallback, type ReactNode } from 'react';
import { useWallet } from '../WalletContext.js';
import { passwordStrength } from '../crypto.js';

export function ImportWallet({ onBack }: { onBack: () => void }): ReactNode {
  const { actions } = useWallet();
  const [mnemonic, setMnemonic] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const strength = passwordStrength(password);
  const wordCount = mnemonic.trim().split(/\s+/).filter(w => w.length > 0).length;
  const isValidWordCount = wordCount === 12 || wordCount === 24;

  const handleImport = useCallback(async () => {
    setError('');

    const normalizedMnemonic = mnemonic.trim().toLowerCase().replace(/\s+/g, ' ');

    if (!isValidWordCount) {
      setError('Recovery phrase must be 12 or 24 words');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await actions.importWallet(normalizedMnemonic, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import wallet');
    } finally {
      setLoading(false);
    }
  }, [mnemonic, password, confirmPassword, isValidWordCount, actions]);

  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      setMnemonic(text.trim());
    } catch {
      // Clipboard API not available or denied
    }
  }, []);

  return (
    <div className="screen">
      <div className="screen-header">
        <button className="btn-ghost" onClick={onBack}>Back</button>
        <h2>Import Wallet</h2>
      </div>
      <div className="screen-body">
        <p className="text-secondary">
          Enter your 12 or 24 word recovery phrase to restore your wallet.
        </p>

        <div className="form-group">
          <label>
            Recovery Phrase
            <span className="text-muted"> ({wordCount} words)</span>
          </label>
          <textarea
            value={mnemonic}
            onChange={e => setMnemonic(e.target.value)}
            placeholder="Enter your recovery phrase, words separated by spaces"
            rows={4}
            autoFocus
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
          />
          <button className="btn-ghost btn-sm" onClick={() => void handlePaste()}>
            Paste from clipboard
          </button>
        </div>

        <div className="form-group">
          <label>New Password</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Choose a password (min 8 characters)"
          />
          {password.length > 0 && (
            <div className={`strength-meter strength-${strength.score}`}>
              <div className="strength-bar" style={{ width: `${(strength.score / 4) * 100}%` }} />
              <span className="strength-label">{strength.label}</span>
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Confirm Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder="Re-enter password"
            onKeyDown={e => e.key === 'Enter' && void handleImport()}
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <button
          className="btn-primary btn-full"
          onClick={() => void handleImport()}
          disabled={loading || !isValidWordCount || password.length < 8}
        >
          {loading ? 'Importing...' : 'Import Wallet'}
        </button>
      </div>
    </div>
  );
}
